import { Input } from 'antd';
import { TextAreaProps } from 'antd/es/input/TextArea';
import { IMapIrSearchResult, mapIrSearchAddress } from 'global/helperFunctions/mapIrFunctions';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

const { TextArea } = Input;

// ── local address keywords ──────────────────────────────────────────────
const ADDRESS_KEYWORDS = [
  'کوچه',
  'خیابان',
  'میدان',
  'بلوار',
  'بزرگراه',
  'چهارراه',
  'سه‌راه',
  'بن‌بست',
  'فرعی',
  'اصلی',
  'پلاک',
  'طبقه',
  'واحد',
  'برج',
  'مجتمع',
  'شهرک',
  'محله',
  'فلکه',
  'جنب',
  'روبروی',
  'نرسیده به',
  'ابتدای',
  'انتهای',
  'بین',
  'تقاطع',
  'نبش',
  'درب',
  'زنگ',
  'همکف',
  'ورودی',
  'گذر',
  'میدانچه',
  'بازارچه',
  'شهر تهران',
  'شهر مشهد',
  'شهر اصفهان',
  'شهر شیراز',
  'شهر تبریز',
  'شهر کرج',
  'شهر اهواز',
  'شهر قم',
  'شهر کرمانشاه',
  'شهر رشت',
  'تهران',
  'مشهد',
  'اصفهان',
  'شیراز',
  'تبریز',
  'کرج',
  'اهواز',
  'قم',
  'کرمانشاه',
  'رشت',
  'استان تهران',
  'استان',
  'منطقه',
  'کیلومتر',
  'جاده',
  'اتوبان',
  'رمپ',
  'دوربرگردان',
  'زیرگذر',
  'روگذر',
  'پارکینگ',
  'مجتمع تجاری',
  'مجتمع مسکونی',
  'ساختمان',
  'پاساژ',
  'سرای',
  'بازار',
];

// ── city names used for smart filtering ───────────────────────────────
// ordered longest-first so "شهر تهران" matches before "تهران"
const CITY_KEYWORDS = [
  'شهر تهران',
  'شهر مشهد',
  'شهر اصفهان',
  'شهر شیراز',
  'شهر تبریز',
  'شهر کرج',
  'شهر اهواز',
  'شهر قم',
  'شهر کرمانشاه',
  'شهر رشت',
  'استان تهران',
  'تهران',
  'مشهد',
  'اصفهان',
  'شیراز',
  'تبریز',
  'کرج',
  'اهواز',
  'قم',
  'کرمانشاه',
  'رشت',
];

// core city names (without prefix) used for cross-matching
const CORE_CITIES = ['تهران', 'مشهد', 'اصفهان', 'شیراز', 'تبریز', 'کرج', 'اهواز', 'قم', 'کرمانشاه', 'رشت'];

interface ITcAddressAutocomplete extends TextAreaProps {
  onSelectLocation?: (_location: IMapIrSearchResult) => void;
}

// ── helper: get the last "word" being typed (after last space, comma, or newline) ──
const getLastTypingWord = (text: string): { prefix: string; word: string } => {
  const lastDelimiter = Math.max(text.lastIndexOf(' '), text.lastIndexOf('،'), text.lastIndexOf('\n'), text.lastIndexOf(','));
  if (lastDelimiter === -1) {
    return { prefix: '', word: text };
  }
  return {
    prefix: text.slice(0, lastDelimiter + 1),
    word: text.slice(lastDelimiter + 1),
  };
};

// ── helper: detect which city the address already contains ────────────
// returns the core city name (e.g. "تهران") or null
const detectCityInText = (text: string, currentWord: string): string | null => {
  // exclude the current word being typed from detection
  const textWithoutCurrent = text.slice(0, text.length - currentWord.length).trim();
  if (!textWithoutCurrent) return null;
  // check longest city keywords first for correct matching
  for (const kw of CITY_KEYWORDS) {
    if (textWithoutCurrent.includes(kw)) {
      for (const city of CORE_CITIES) {
        if (kw.includes(city)) return city;
      }
      return kw;
    }
  }
  return null;
};

const TcAddressAutocomplete: FC<ITcAddressAutocomplete> = ({ onSelectLocation, onChange, ...props }) => {
  // ── states ──────────────────────────────────────────────────────────
  const [apiSuggestions, setApiSuggestions] = useState<IMapIrSearchResult[]>([]);
  const [localSuggestions, setLocalSuggestions] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [query, setQuery] = useState('');

  // ── refs ────────────────────────────────────────────────────────────
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const latestRequestIdRef = useRef(0);
  const lastEmittedValueRef = useRef<string>('');

  // ── computed ────────────────────────────────────────────────────────
  const { word: currentWord } = getLastTypingWord(query);
  const detectedCity = useMemo(() => detectCityInText(query, currentWord), [query, currentWord]);

  // merge local + api for a single flat list; locals first
  const allSuggestions = useMemo(() => {
    const combined: Array<{ type: 'local'; text: string } | { type: 'api'; item: IMapIrSearchResult }> = [];
    localSuggestions.forEach((kw) => combined.push({ type: 'local', text: kw }));
    apiSuggestions.forEach((item) => combined.push({ type: 'api', item }));
    return combined;
  }, [localSuggestions, apiSuggestions]);

  // ── effects ─────────────────────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // sync external value changes (e.g. from map reverse geocode)
  useEffect(() => {
    const externalValue = (props.value as string) || '';
    if (externalValue && externalValue !== lastEmittedValueRef.current && externalValue !== query) {
      setQuery(externalValue);
      lastEmittedValueRef.current = externalValue;
    }
  }, [props.value]);

  // ── api call ────────────────────────────────────────────────────────
  const fetchApiSuggestions = useCallback(
    async (text: string, city?: string | null) => {
      const requestId = ++latestRequestIdRef.current;
      // if a city is already in the address, bias the search toward it
      const searchText = city ? `${city} ${text}` : text;
      // drop API rows with no displayable text — avoids empty rows and undefined lookups in render
      const results = (await mapIrSearchAddress(searchText)).filter((r) => r.address || r.value || r.text);
      if (requestId !== latestRequestIdRef.current) return;

      // client-side filter: if a city is detected, keep only matching results
      let filtered = results;
      if (city) {
        filtered = results.filter((r) => {
          const resultCity = r.city || '';
          const resultAddress = r.address || '';
          const resultCounty = r.county || '';
          return resultCity.includes(city) || resultAddress.includes(city) || resultCounty.includes(city);
        });
        // if filtering removed everything, fall back to all results
        if (filtered.length === 0) filtered = results;
      }

      setApiSuggestions(filtered);
      if (filtered.length > 0 || localSuggestions.length > 0) {
        setIsOpen(true);
      }
      setActiveIndex(-1);
    },
    [localSuggestions.length]
  );

  // ── local keyword filter ────────────────────────────────────────────
  const filterLocalKeywords = useCallback((text: string) => {
    const { word } = getLastTypingWord(text);
    if (!word || word.length < 1) {
      setLocalSuggestions([]);
      return;
    }

    const city = detectCityInText(text, word);

    let filtered = ADDRESS_KEYWORDS.filter((kw) => kw.startsWith(word) && kw !== word);

    // if a city is already in the address, exclude keywords of other cities
    if (city) {
      filtered = filtered.filter((kw) => {
        for (const otherCity of CORE_CITIES) {
          if (otherCity !== city && kw.includes(otherCity)) {
            return false;
          }
        }
        return true;
      });
    }

    filtered = filtered.slice(0, 6);
    setLocalSuggestions(filtered);
    if (filtered.length > 0) {
      setIsOpen(true);
    }
    setActiveIndex(-1);
  }, []);

  // ── handlers ────────────────────────────────────────────────────────
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setQuery(value);
    onChange && onChange(e);

    // local keywords — instant filtering
    filterLocalKeywords(value);

    // api search — debounced
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length >= 2) {
      const city = detectCityInText(value, getLastTypingWord(value).word);
      debounceRef.current = setTimeout(() => fetchApiSuggestions(value.trim(), city), 500);
    } else {
      setApiSuggestions([]);
      if (localSuggestions.length === 0) setIsOpen(false);
    }
  };

  /** Selecting a local keyword — replace only the last word, keep the rest of the text */
  const handleSelectLocal = (keyword: string) => {
    const { prefix } = getLastTypingWord(query);
    const newText = prefix + keyword + ' ';

    lastEmittedValueRef.current = newText;
    onChange &&
      onChange({
        target: { value: newText },
      } as React.ChangeEvent<HTMLTextAreaElement>);
    setQuery(newText);

    // re-trigger api search with the new text, respecting detected city
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (newText.trim().length >= 2) {
      const city = detectCityInText(newText, '');
      debounceRef.current = setTimeout(() => fetchApiSuggestions(newText.trim(), city), 300);
    }

    setLocalSuggestions([]);
    setActiveIndex(-1);
  };

  /** Selecting an API result — replace the entire address */
  const handleSelectApi = (item: IMapIrSearchResult) => {
    const addressText = item.address || item.value || '';

    lastEmittedValueRef.current = addressText;
    onChange &&
      onChange({
        target: { value: addressText },
      } as React.ChangeEvent<HTMLTextAreaElement>);
    setQuery(addressText);

    onSelectLocation && onSelectLocation(item);

    setIsOpen(false);
    setApiSuggestions([]);
    setLocalSuggestions([]);
  };

  // ── keyboard ────────────────────────────────────────────────────────
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!isOpen || allSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => (prev < allSuggestions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : allSuggestions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < allSuggestions.length) {
          const selected = allSuggestions[activeIndex];
          if (selected.type === 'local') {
            handleSelectLocal(selected.text);
          } else {
            handleSelectApi(selected.item);
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  // ── helpers ─────────────────────────────────────────────────────────
  const highlightMatch = (text?: string, searchQuery?: string) => {
    if (!text) return null;
    if (!searchQuery) return text;
    const idx = text.indexOf(searchQuery);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className='font-bold text-blue-600'>{text.slice(idx, idx + searchQuery.length)}</span>
        {text.slice(idx + searchQuery.length)}
      </>
    );
  };

  // ── render ──────────────────────────────────────────────────────────
  const hasSuggestions = isOpen && allSuggestions.length > 0;

  return (
    <div ref={wrapperRef} className='relative w-full'>
      <TextArea rows={3} {...props} value={query || props.value} onChange={handleChange} onKeyDown={handleKeyDown} onFocus={() => allSuggestions.length > 0 && setIsOpen(true)} />

      {hasSuggestions && (
        <div className='absolute z-50 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-lg max-h-72'>
          {detectedCity && (
            <div className='px-3 py-1.5 text-xs text-blue-600 bg-blue-50 border-b border-blue-100'>
              📍 محدود به <strong>{detectedCity}</strong>
            </div>
          )}
          {allSuggestions.map((suggestion, index) =>
            suggestion.type === 'local' ? (
              // ── local keyword row ──────────────────────────────────
              <div
                key={`local-${index}`}
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 text-sm
                  ${index === activeIndex ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectLocal(suggestion.text);
                }}
                onMouseEnter={() => setActiveIndex(index)}>
                <span className='inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-green-500 rounded-full flex-shrink-0'>م</span>
                <span className='text-gray-800'>{highlightMatch(suggestion.text, currentWord)}</span>
              </div>
            ) : (
              // ── Map.ir API row ─────────────────────────────────────
              <div
                key={`api-${index}`}
                className={`flex flex-col px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 text-sm
                  ${index === activeIndex ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectApi(suggestion.item);
                }}
                onMouseEnter={() => setActiveIndex(index)}>
                <span className='text-gray-800'>{highlightMatch(suggestion.item.address || suggestion.item.value, query)}</span>
                {suggestion.item.city && (
                  <span className='text-xs text-gray-500 mt-0.5'>{[suggestion.item.city, suggestion.item.region, suggestion.item.neighbourhood].filter(Boolean).join('، ')}</span>
                )}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default TcAddressAutocomplete;
